
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Divider
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ConfirmationDialog from "../../../../../components/common/ConfirmationDialog";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../../store";
import { addCustomFormField, getCustomFormFields } from "../../../../../modules/masterdata/actions";
import {
  selectAddCustomFormFieldLoading,
  selectAddCustomFormFieldSuccess,
  selectAddCustomFormFieldError,
  selectUpdateCustomFormFieldLoading,
  selectUpdateCustomFormFieldSuccess,
  selectUpdateCustomFormFieldError
} from "../../../../../modules/masterdata/selectors";

import ResponsiveSelect from "../../../../../components/ResponsiveSelect";
import { ICustomFormField } from "../../../../../modules/masterdata/types";
import { updateCustomFormField } from "../../../../../modules/masterdata/actions";

interface AddCustomFormFieldModalProps {
  open: boolean;
  onClose: () => void;
  formId: number;
  fieldToEdit?: ICustomFormField | null;
}

const AddCustomFormFieldModal: React.FC<AddCustomFormFieldModalProps> = ({ open, onClose, formId, fieldToEdit }) => {
  const dispatch = useDispatch<AppDispatch>();
  const addLoading = useSelector(selectAddCustomFormFieldLoading);
  const addSuccess = useSelector(selectAddCustomFormFieldSuccess);
  const addError = useSelector(selectAddCustomFormFieldError);
  const updateLoading = useSelector(selectUpdateCustomFormFieldLoading);
  const updateSuccess = useSelector(selectUpdateCustomFormFieldSuccess);
  const updateError = useSelector(selectUpdateCustomFormFieldError);

  const loading = addLoading || updateLoading;
  const error = addError || updateError;
  const isEditMode = !!fieldToEdit;

  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [isRequired, setIsRequired] = useState(true);

  const [fieldOptions, setFieldOptions] = useState<string[]>([]);
  const [currentOption, setCurrentOption] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState<string | null>(null);
  const showOptionsInput = ["dropdown", "radio", "checkbox"].includes(fieldType);

  // Field options not implemented in UI yet as per basic requirement, 
  // but could be added here for dropdown types.

  useEffect(() => {
    if (fieldToEdit) {
      setFieldName(fieldToEdit.field_name);
      setFieldType(fieldToEdit.field_type);
      setIsRequired(fieldToEdit.is_required);
      if (fieldToEdit.field_options) {
        setFieldOptions(fieldToEdit.field_options);
      } else {
        setFieldOptions([]);
      }
    } else {
      setFieldName("");
      setFieldType("text");
      setIsRequired(true);
      setFieldOptions([]);
      setCurrentOption("");
    }
  }, [fieldToEdit, open]); // Added open to reset when opening for add

  useEffect(() => {
    if (addSuccess || updateSuccess) {
      dispatch(getCustomFormFields(formId));
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSuccess, updateSuccess, dispatch, formId]);

  const handleClose = () => {
    setFieldName("");
    setFieldType("text");
    setIsRequired(true);
    setFieldOptions([]);
    setCurrentOption("");
    setCurrentOption("");
    onClose();
  };

  const handleAddOption = () => {
    if (currentOption.trim()) {
      setFieldOptions([...fieldOptions, currentOption.trim()]);
      setCurrentOption("");
    }
  };

  const handleDeleteClick = (option: string) => {
    setOptionToDelete(option);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (optionToDelete) {
      setFieldOptions(fieldOptions.filter(o => o !== optionToDelete));
    }
    setIsDeleteDialogOpen(false);
    setOptionToDelete(null);
  };

  const handleSubmit = () => {
    if (!fieldName) return;

    const payload: any = {
      form_id: formId,
      field_name: fieldName,
      field_type: fieldType,
      is_required: isRequired
    };

    if (["dropdown", "radio", "checkbox"].includes(fieldType) && fieldOptions.length > 0) {
      payload.field_options = fieldOptions;
    }

    if (isEditMode && fieldToEdit) {
      dispatch(updateCustomFormField({ ...payload, field_id: fieldToEdit.field_id }));
    } else {
      dispatch(addCustomFormField(payload));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? "Edit Custom Form Field" : "Add Custom Form Field"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Field Name"
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            fullWidth
            required
          />

          <ResponsiveSelect
            label="Field Type"
            value={fieldType}
            onChange={(val) => setFieldType(val)}
            options={[
              { value: "text", label: "Text" },
              { value: "number", label: "Number" },
              { value: "date", label: "Date" },
              { value: "dropdown", label: "Dropdown" },
              { value: "radio", label: "Radio" },
              { value: "checkbox", label: "Checkbox" },
            ]}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#5E8E7E",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#5E8E7E",
                  },
                }}
              />
            }
            label="Is Required"
          />
          {error && <Box color="error.main">{error}</Box>}

          {showOptionsInput && (
            <Box sx={{ border: "1px solid #eee", p: 2, borderRadius: 1 }}>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Option value"
                  value={currentOption}
                  onChange={(e) => setCurrentOption(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddOption}
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{
                    bgcolor: "#5E8E7E",
                    color: "white",
                    "&:hover": { bgcolor: "#4A7A6A" },
                    textTransform: "none",
                    px: 2,
                  }}
                >
                  ADD
                </Button>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {fieldOptions.map((option, index) => (
                  <React.Fragment key={index}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
                      <Typography sx={{ color: "#333" }}>{option}</Typography>
                      <IconButton size="small" onClick={() => handleDeleteClick(option)} sx={{ color: "#E57373" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {index < fieldOptions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {fieldOptions.length === 0 && (
                  <Box sx={{ p: 1, textAlign: "center", color: "text.secondary", fontSize: "0.875rem" }}>
                    No options available
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={!!loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!!loading || !fieldName}
          sx={{ bgcolor: "#5E8E7E", "&:hover": { bgcolor: "#4A7A6A" }, px: 3 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : (isEditMode ? "UPDATE FIELD" : "ADD FIELD")}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Remove Option"
        message={`Are you sure you want to remove the option "${optionToDelete}"?`}
      />
    </Dialog>
  );
};

export default AddCustomFormFieldModal;
